import { getClient } from "@/lib/sanity/server";
import { groq } from "next-sanity";
import { useRouter } from "next/router";
import { useEffect } from "react";
import useSWR from "swr";
import { z } from "zod";
import RelatedProducts from "./RelatedProducts";
import { Container, Heading, RelatedWrapper, Title } from "./styles";

const PAGE_SIZE = 20;

const featuredProductsBodyQuery = groq`
[$start...$end] {
  _id,
  title,
  "slug": slug.current,
  thumbnail {
    asset-> {
      url,
      metadata,
      "alt": coalesce(alt[$locale], alt[$defaultLocale])
    }
  },
  "param1": parameters[attribute._ref == "product-type"][0] {
    "name": attribute->.name,
    values,
  },
  "param2": parameters[attribute._ref == "route-of-administration"][0] {
    "name": attribute->.name,
    values,
  },
  "param3": parameters[attribute._ref == "material"][0] {
    "name": attribute->.name,
    values,
  },
  "eco": parameters[attribute._ref == "ecopositive"][0].values[0],
}`;

const relatedProductsQueryDataSchema = z
  .array(
    z.object({
      _id: z.string(),
      title: z.string(),
      slug: z.string(),
      thumbnail: z.object({
        asset: z.object({
          alt: z.string().nullable(),
          metadata: z
            .object({
              dimensions: z.object({
                width: z.number(),
                height: z.number(),
              }),
            })
            .nullable(),
          url: z.string(),
        }),
      }),
      param1: z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
      param2: z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
      param3: z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
      eco: z.string().nullable(),
    })
  )
  .nullable();

type RelatedProductsData = z.infer<typeof relatedProductsQueryDataSchema>;

const fetcher = async (
  query: string,
  params: {
    start: number;
    end: number;
    locale: string;
    defaultLocale: string;
  }
) => {
  const res = await getClient().fetch(query, params);
  return relatedProductsQueryDataSchema.parse(res);
};

export type SectionProps = {
  title: string;
  _id: string;
  rules: [
    {
      attributeQuery: any;
      attributeProduct: any;
      value: string;
    }
  ];
};

type ProductProps = {
  correlatedParameters: {
    attributeKey: string;
    values: string[];
  }[];
};

type Props = {
  section: SectionProps;
  product: ProductProps;
  headingRef: any;
};

const Section: React.FC<Props> = ({ product, section, headingRef }) => {
  const { locale = "it", defaultLocale = "it" } = useRouter();
  let params = {
    start: 0,
    end: PAGE_SIZE,
    locale,
    defaultLocale,
  };
  let hideForThisProduct = false;
  let selectQuery = "*[_type == 'product'";
  const selectAttributeQuery =
    "'$value' in parameters[@.attribute._key == '$key'].values[]";

  const getProductAttributeValueByAttributeName = (name: string): string[] => {
    for (const parameter of product.correlatedParameters) {
      if (name === parameter.attributeKey) return parameter.values;
    }
    return [];
  };

  section.rules.forEach((rule) => {
    if (rule.attributeQuery && (rule.attributeProduct || rule.value)) {
      // if the rule value is set I exclude the section that has the same attribute query value of the current product
      // For example if the current product type is Containers, I exclude the section with this product type
      if (rule.value) {
        const currentValueToExclude = getProductAttributeValueByAttributeName(
          rule.attributeQuery
        );
        if (currentValueToExclude.includes(rule.value))
          hideForThisProduct = true;
      }

      let valuesToSearch = [rule.value];
      if (rule.attributeProduct)
        valuesToSearch = getProductAttributeValueByAttributeName(
          rule.attributeProduct
        );
      let attributeAndQuery = "";
      if (valuesToSearch.length) {
        attributeAndQuery = "(";
        valuesToSearch.forEach((valueToSearch, index) => {
          attributeAndQuery += selectAttributeQuery
            .replace("$value", valueToSearch)
            .replace("$key", rule.attributeQuery);
          if (index < valuesToSearch.length - 1) attributeAndQuery += " || ";
        });
        attributeAndQuery += ")";

        selectQuery += ` && ${attributeAndQuery}`;
      }
    }
  });
  selectQuery += `]${featuredProductsBodyQuery}`;

  let { data, error } = useSWR([selectQuery, params], fetcher);

  useEffect(() => {
    if (error) console.error(error);
  }, [error]);

  useEffect(() => {
    // TODO: ask alex why this is needed
    const headingElement = headingRef.current;
    headingElement.style.display = "block";
  });

  if (!data || data.length === 0 || hideForThisProduct) return null;

  return (
    <Container>
      <Heading>
        <Title>{section.title}</Title>
      </Heading>
      <RelatedWrapper>
        {data && <RelatedProducts products={data} />}
      </RelatedWrapper>
    </Container>
  );
};

export default Section;
